import { types, flow, getEnv, getSnapshot, getRoot } from 'mobx-state-tree';
import { createComputedGetter, parseFormula } from './formulaParser.js';

/**
 * Генератор MST моделей из JSON Schema
 * Создает базовую структуру модели с правильными типами
 */

// Кастомный тип для дат - автоматически преобразует ISO строки в Date
const DateType = types.custom({
    name: 'DateType',
    fromSnapshot(value) {
        if (value === null || value === undefined) return null;
        if (value instanceof Date) return value;
        // Преобразуем ISO строку в Date объект
        return new Date(value);
    },
    toSnapshot(value) {
        if (value === null || value === undefined) return null;
        // Сохраняем как ISO строку для отправки на сервер
        return value instanceof Date ? value.toISOString() : value;
    },
    isTargetType(value) {
        return value instanceof Date || typeof value === 'string' || value === null;
    },
    getValidationMessage(value) {
        if (value === null || value === undefined) return '';
        if (value instanceof Date) return '';
        if (typeof value === 'string') {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
                return 'Invalid date string';
            }
            return '';
        }
        return 'Value must be a Date, date string, or null';
    }
});

// Маппинг JSON Schema типов в MST типы
const typeMapping = {
    string: () => types.string,
    number: () => types.number,
    integer: () => types.integer,
    boolean: () => types.boolean,
    array: (items) => types.array(getMSTType(items)),
    object: (properties) => types.frozen(), // Для вложенных объектов используем frozen
};

// Получение MST типа из JSON Schema определения
function getMSTType(schemaDef, fieldName, isArrayItem = false) {
    if (!schemaDef) return types.frozen();

    const { type, format, enum: enumValues, items, properties } = schemaDef;
    
    // Обработка enum
    if (enumValues && enumValues.length > 0) {
        return types.enumeration(enumValues);
    }
    
    // Обработка форматов
    if (type === 'string' && format === 'date-time') {
        // Используем кастомный тип для автоматического преобразования
        return DateType;
    }
    
    // Обработка массивов
    if (type === 'array' && items) {
        return types.array(getMSTType(items, fieldName, true)); // Передаем флаг что это элемент массива
    }
    
    // Обработка объектов - создаем модель для вложенных структур
    if (type === 'object' && properties) {
        // Создаем вложенную модель для объекта с учетом дефолтных значений
        const nestedProps = {};
        const required = schemaDef.required || [];

        for (const [key, value] of Object.entries(properties)) {
            // Для вычисляемых полей не создаем props - они будут в views
            // Но MST может требовать их наличия в некоторых случаях
            if (value['x-calculated']) {
                // Пропускаем - вычисляемые поля будут добавлены как views
                continue;
            }

            let nestedType = getMSTType(value, key, false);

            // Обработка дефолтных значений для вложенных полей
            if ('default' in value) {
                nestedType = types.optional(nestedType, value.default);
            } else if (!required.includes(key)) {
                // Необязательные поля без дефолтов делаем maybe
                nestedType = types.maybe(nestedType);
            }

            nestedProps[key] = nestedType;
        }

        let model = types.model(nestedProps);

        // Добавляем views для вычисляемых полей
        const calculatedFields = {};
        for (const [key, value] of Object.entries(properties)) {
            if (value['x-calculated'] && value['x-formula']) {
                calculatedFields[key] = value;
            }
        }

        if (Object.keys(calculatedFields).length > 0) {
            model = model.views(self => {
                const views = {};
                for (const [key, fieldDef] of Object.entries(calculatedFields)) {
                    // Используем геттер для MST views
                    Object.defineProperty(views, key, {
                        get: function() {
                            return parseFormula(fieldDef['x-formula'], self);
                        },
                        enumerable: true,
                        configurable: true
                    });
                }
                return views;
            });
        }

        // Если это объект внутри массива - добавляем views и actions
        if (isArrayItem) {
            // Добавляем views для вычисляемых полей
            const calculatedFields = {};
            for (const [key, value] of Object.entries(properties)) {
                if (value['x-calculated'] && value['x-formula']) {
                    calculatedFields[key] = value['x-formula'];
                    console.log(`[schemaToMST] Found calculated field in array item: ${key} = ${value['x-formula']}`);
                }
            }

            if (Object.keys(calculatedFields).length > 0) {
                console.log(`[schemaToMST] Adding views for array item with fields:`, calculatedFields);
                model = model.views(self => {
                    const views = {};
                    for (const [key, formula] of Object.entries(calculatedFields)) {
                        // Используем геттер для MST views
                        Object.defineProperty(views, key, {
                            get: function() {
                                return parseFormula(formula, self);
                            },
                            enumerable: true,
                            configurable: true
                        });
                    }
                    return views;
                });
            }

            // Добавляем actions для изменяемых полей
            model = model.actions(self => {
                const actions = {};
                for (const [key, value] of Object.entries(properties)) {
                    // Пропускаем id, вычисляемые и readonly поля
                    if (key !== 'id' && !value['x-calculated'] && !value['x-readonly']) {
                        // Генерируем setter автоматически
                        const setterName = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
                        actions[setterName] = (value) => {
                            self[key] = value;
                        };
                    }
                }
                return actions;
            });
        }

        return model;
    } else if (type === 'object') {
        // Для объектов без определенной структуры используем frozen
        return types.frozen();
    }
    
    // Базовые типы
    const mstType = typeMapping[type];
    return mstType ? mstType(items || properties) : types.frozen();
}

// Создание props объекта из JSON Schema
function createPropsFromSchema(schema) {
    const props = {};
    const { properties = {}, required = [] } = schema;

    for (const [key, value] of Object.entries(properties)) {
        // Пропускаем служебные поля UI
        // Для computed полей проверяем наличие x-trigger-calculated
        if (key.startsWith('x-') || key === 'passwordHash' || key === 'passwordConfirm') {
            continue;
        }

        // Пропускаем чистые computed поля (без x-trigger-calculated)
        if (value['x-calculated'] && !value['x-trigger-calculated']) {
            continue;
        }

        // Проверяем, является ли объект контейнером только для вычисляемых полей
        if (value.type === 'object' && value.properties) {
            const allFieldsCalculated = Object.values(value.properties).every(
                field => field['x-calculated']
            );
            if (allFieldsCalculated) {
                // Пропускаем объект, который содержит только вычисляемые поля
                // Он будет добавлен как view
                continue;
            }
        }

        // Определяем тип
        let mstType = getMSTType(value, key); // Передаём имя поля для контекста
        
        // Обработка x-generated полей - они могут быть undefined до получения с сервера
        if (value['x-generated']) {
            mstType = types.maybe(mstType);
        }
        // Добавляем default значение если есть
        else if ('default' in value) {
            mstType = types.optional(mstType, value.default);
        } else if (!required.includes(key)) {
            // Необязательные поля
            // Для объектов и массивов используем дефолтные значения
            if (value.type === 'object') {
                const objDefault = {};
                if (value.properties) {
                    for (const [propKey, propVal] of Object.entries(value.properties)) {
                        if (propVal.type === 'array') {
                            objDefault[propKey] = [];
                        }
                    }
                }
                mstType = types.optional(mstType, objDefault);
            } else if (value.type === 'array') {
                mstType = types.optional(mstType, []);
            } else {
                // Для примитивов используем maybe
                mstType = types.maybe(mstType);
            }
        }
        
        // Особая обработка для id - делаем identifier
        if (key === 'id') {
            mstType = types.identifier;
        }
        
        props[key] = mstType;
    }
    
    return props;
}

// Получение дефолтного значения по типу
function getDefaultValue(type) {
    switch (type) {
        case 'string': return '';
        case 'number':
        case 'integer': return 0;
        case 'boolean': return false;
        case 'array': return [];
        case 'object': return {};
        default: return undefined;
    }
}

// Универсальная функция замены плейсхолдеров
function replacePlaceholdersUniversal(obj, timestamp, index) {
    for (const key in obj) {
        if (typeof obj[key] === 'string') {
            // Заменяем плейсхолдеры
            let value = obj[key].replace('{{timestamp}}', timestamp);

            // Для order используем index как есть (начиная с 0)
            // Для остальных полей (включая name) используем index + 1 (для отображения)
            if (key === 'order') {
                value = value.replace('{{index}}', index);
                if (!isNaN(value)) {
                    obj[key] = Number(value);
                } else {
                    obj[key] = value;
                }
            } else {
                value = value.replace('{{index}}', index + 1);
                obj[key] = value;
            }
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            // Рекурсивно обрабатываем вложенные объекты
            replacePlaceholdersUniversal(obj[key], timestamp, index);
        }
    }
    return obj;
}

// Генерация actions из x-operations конфигурации
function generateActionsFromOperations(self, schema) {
    const operations = schema['x-operations'];
    if (!operations) {
        console.log('[generateActionsFromOperations] No x-operations found in schema');
        return {};
    }

    console.log('[generateActionsFromOperations] Processing operations:', operations);
    const actions = {};

    // Вспомогательная функция для получения значения по пути
    const getByPath = (obj, path) => {
        const parts = path.split('.');
        let current = obj;
        for (const part of parts) {
            if (part.includes('[]')) {
                // Пропускаем [] для вложенных массивов
                const cleanPart = part.replace('[]', '');
                current = current?.[cleanPart];
            } else {
                current = current?.[part];
            }
        }
        return current;
    };

    // Вспомогательная функция для установки значения по пути
    const setByPath = (obj, path, value) => {
        const parts = path.split('.');
        let current = obj;
        for (let i = 0; i < parts.length - 1; i++) {
            const part = parts[i].replace('[]', '');
            if (!current[part]) {
                current[part] = {};
            }
            current = current[part];
        }
        const lastPart = parts[parts.length - 1].replace('[]', '');
        current[lastPart] = value;
    };

    // Обработка каждой операции
    for (const [path, config] of Object.entries(operations)) {
        const { type, operations: ops, itemTemplate, parent, fields } = config;

        // Генерация имен действий на основе пути
        const pathParts = path.split('.');
        const entityName = pathParts[pathParts.length - 1].replace('[]', '');
        const singularName = entityName.endsWith('s') ? entityName.slice(0, -1) : entityName;
        const capitalizedName = singularName.charAt(0).toUpperCase() + singularName.slice(1);

        if (type === 'array' && ops.includes('add')) {
            // Добавление элемента в массив
            actions[`add${capitalizedName}`] = () => {
                // Инициализируем путь если нужно
                const pathParts = path.split('.');
                let current = self;

                for (let i = 0; i < pathParts.length - 1; i++) {
                    const part = pathParts[i].replace('[]', '');
                    if (!current[part]) {
                        // Создаем объект с правильной структурой
                        current[part] = {};
                    }
                    current = current[part];
                }

                const lastPart = pathParts[pathParts.length - 1].replace('[]', '');
                if (!current[lastPart]) {
                    current[lastPart] = [];
                }

                let array = current[lastPart];

                // Подготовка шаблона с заменой плейсхолдеров
                const newItem = JSON.parse(JSON.stringify(itemTemplate));
                const timestamp = Date.now();
                const index = array?.length || 0;

                // Используем универсальную функцию замены
                // Передаем index как есть, функция сама решит где добавить +1
                replacePlaceholdersUniversal(newItem, timestamp, index);

                if (Array.isArray(array)) {
                    array.push(newItem);
                }
                return newItem;
            };
        }

        if (type === 'array' && ops.includes('remove')) {
            // Удаление элемента из массива
            actions[`remove${capitalizedName}`] = (itemId) => {
                const array = getByPath(self, path);
                if (array) {
                    const index = array.findIndex(item => item.id === itemId);
                    if (index !== -1) {
                        array.splice(index, 1);
                    }
                }
            };
        }

        if (type === 'array' && ops.includes('update')) {
            // Обновление элемента в массиве
            actions[`update${capitalizedName}`] = (itemId, field, value) => {
                const array = getByPath(self, path);
                if (array) {
                    const item = array.find(item => item.id === itemId);
                    if (item) {
                        item[field] = value;
                    }
                }
            };
        }

        if (type === 'array' && ops.includes('reorder')) {
            // Перемещение элементов в массиве - пересчитываем order для всех
            actions[`reorder${capitalizedName}`] = (draggedId, targetId) => {
                const array = getByPath(self, path);
                if (array) {
                    const draggedIndex = array.findIndex(item => item.id === draggedId);
                    const targetIndex = array.findIndex(item => item.id === targetId);

                    console.log(`[MST reorder${capitalizedName}] Before:`, {
                        draggedId,
                        targetId,
                        draggedIndex,
                        targetIndex,
                        orders: array.map(item => ({ id: item.id, order: item.order }))
                    });

                    if (draggedIndex !== -1 && targetIndex !== -1 && draggedIndex !== targetIndex) {
                        // Просто меняем order местами между двумя элементами
                        const draggedItem = array[draggedIndex];
                        const targetItem = array[targetIndex];

                        // Если order не определен, устанавливаем на основе индексов
                        if (draggedItem.order === undefined) draggedItem.order = draggedIndex;
                        if (targetItem.order === undefined) targetItem.order = targetIndex;

                        // Меняем местами order
                        const tempOrder = draggedItem.order;
                        draggedItem.order = targetItem.order;
                        targetItem.order = tempOrder;

                        console.log(`[MST reorder${capitalizedName}] After:`, {
                            orders: array.map(item => ({ id: item.id, order: item.order }))
                        });
                    }
                }
            };
        }

        if (type === 'nestedArray') {
            // Для вложенных массивов (например, works внутри stages)
            const parentPath = parent;
            const nestedField = pathParts[pathParts.length - 1].replace('[]', '');
            const nestedSingular = nestedField.endsWith('s') ? nestedField.slice(0, -1) : nestedField;
            const nestedCapitalized = nestedSingular.charAt(0).toUpperCase() + nestedSingular.slice(1);

            if (ops.includes('add')) {
                actions[`add${nestedCapitalized}`] = (parentId) => {
                    const parentArray = getByPath(self, parentPath);
                    if (parentArray) {
                        const parentItem = parentArray.find(item => item.id === parentId);
                        if (parentItem) {
                            if (!parentItem[nestedField]) {
                                parentItem[nestedField] = [];
                            }

                            const newItem = JSON.parse(JSON.stringify(itemTemplate));
                            const timestamp = Date.now();
                            const index = parentItem[nestedField].length;

                            // Используем универсальную функцию замены
                            // Передаем index как есть, функция сама решит где добавить +1
                            replacePlaceholdersUniversal(newItem, timestamp, index);

                            parentItem[nestedField].push(newItem);
                            return newItem;
                        }
                    }
                };
            }

            if (ops.includes('remove')) {
                actions[`remove${nestedCapitalized}`] = (parentId, itemId) => {
                    const parentArray = getByPath(self, parentPath);
                    if (parentArray) {
                        const parentItem = parentArray.find(item => item.id === parentId);
                        if (parentItem && parentItem[nestedField]) {
                            const index = parentItem[nestedField].findIndex(item => item.id === itemId);
                            if (index !== -1) {
                                parentItem[nestedField].splice(index, 1);
                            }
                        }
                    }
                };
            }

            if (ops.includes('update')) {
                actions[`update${nestedCapitalized}`] = (parentId, itemId, field, value) => {
                    const parentArray = getByPath(self, parentPath);
                    if (parentArray) {
                        const parentItem = parentArray.find(item => item.id === parentId);
                        if (parentItem && parentItem[nestedField]) {
                            const item = parentItem[nestedField].find(item => item.id === itemId);
                            if (item) {
                                item[field] = value;
                            }
                        }
                    }
                };
            }

            if (ops.includes('reorder')) {
                actions[`reorder${nestedCapitalized}`] = (parentId, draggedId, targetId) => {
                    const parentArray = getByPath(self, parentPath);
                    if (parentArray) {
                        const parentItem = parentArray.find(item => item.id === parentId);
                        if (parentItem && parentItem[nestedField]) {
                            const nestedArray = parentItem[nestedField];
                            const draggedIndex = nestedArray.findIndex(item => item.id === draggedId);
                            const targetIndex = nestedArray.findIndex(item => item.id === targetId);

                            console.log(`[MST reorder${nestedCapitalized}] Before:`, {
                                parentId,
                                draggedId,
                                targetId,
                                draggedIndex,
                                targetIndex,
                                orders: nestedArray.map(item => ({ id: item.id, order: item.order }))
                            });

                            if (draggedIndex !== -1 && targetIndex !== -1 && draggedIndex !== targetIndex) {
                                // Просто меняем order местами между двумя элементами
                                const draggedItem = nestedArray[draggedIndex];
                                const targetItem = nestedArray[targetIndex];

                                // Если order не определен, устанавливаем на основе индексов
                                if (draggedItem.order === undefined) draggedItem.order = draggedIndex;
                                if (targetItem.order === undefined) targetItem.order = targetIndex;

                                // Меняем местами order
                                const tempOrder = draggedItem.order;
                                draggedItem.order = targetItem.order;
                                targetItem.order = tempOrder;

                                console.log(`[MST reorder${nestedCapitalized}] After:`, {
                                    orders: nestedArray.map(item => ({ id: item.id, order: item.order }))
                                });
                            }
                        }
                    }
                };
            }
        }

        if (type === 'object' && ops.includes('update')) {
            // Обновление полей объекта
            const objName = entityName.charAt(0).toUpperCase() + entityName.slice(1);
            actions[`update${objName}`] = (field, value) => {
                const obj = getByPath(self, path);
                if (!obj) {
                    setByPath(self, path, {});
                }
                if (fields && fields.includes(field)) {
                    getByPath(self, path)[field] = value;
                }
            };
        }
    }

    return actions;
}

/**
 * Поиск обратной ссылки в целевой схеме
 * @param {object} targetSchema - Схема целевой модели
 * @param {string} sourceEntity - Имя исходной сущности
 * @returns {string|null} Имя поля с обратной ссылкой или null
 */
function findBackReference(targetSchema, sourceEntity) {
    if (!targetSchema?.properties) return null;

    for (const [fieldName, fieldDef] of Object.entries(targetSchema.properties)) {
        const relation = fieldDef['x-relation'];
        if (relation?.entity === sourceEntity && !relation.multiple) {
            return fieldName; // например, 'projectId' для связи calculation->project
        }
    }
    return null;
}

/**
 * Создание геттеров для x-relation полей
 * @param {object} schema - Схема модели
 * @param {string} modelName - Имя текущей модели
 * @returns {object} Объект с геттерами для связанных сущностей
 */
function createRelationGetters(schema, modelName) {
    const getters = {};

    for (const [fieldName, fieldDef] of Object.entries(schema.properties || {})) {
        const relation = fieldDef['x-relation'];
        if (!relation) continue;

        const { entity, field, multiple } = relation;

        // Имя геттера = имя связанной сущности
        const getterName = entity;

        // Пропускаем если геттер уже существует (например, из computed полей)
        if (getters[getterName]) continue;

        getters[getterName] = function() {
            // this будет MST экземпляром при вызове через call(self)
            const root = getRoot(this);
            const targetCollection = root?.collectionManagers?.[entity];

            if (!targetCollection?.items) {
                return multiple ? [] : null;
            }

            if (multiple) {
                // One-to-many: фильтруем по обратному полю
                // Ищем в целевой коллекции поле со ссылкой на нашу модель
                const targetSchema = root?.schemas?.[entity];
                const backRefField = findBackReference(targetSchema, modelName);

                if (backRefField) {
                    // Фильтруем по обратной ссылке (например, calculations по projectId)
                    return targetCollection.items.filter(item =>
                        item[backRefField] === this.id
                    );
                }

                // Если нет обратной ссылки - используем массив IDs из поля
                const ids = this[fieldName] || [];
                return targetCollection.items.filter(item =>
                    ids.includes(item.id)
                );
            } else {
                // Many-to-one: получаем по ID
                const targetId = this[fieldName];
                return targetCollection.getById ?
                    targetCollection.getById(targetId) :
                    targetCollection.items.find(item => item.id === targetId);
            }
        };
    }

    return getters;
}

/**
 * Создание геттеров для дочерних коллекций на основе x-context.parent
 * @param {string} modelName - Имя текущей модели
 * @returns {object} Объект с геттерами для дочерних коллекций
 */
function createChildGetters(modelName) {
    console.log(`[createChildGetters] Creating getter creator for model: ${modelName}`);
    const getters = {};

    // Функция будет вызываться с доступом к root для поиска дочерних моделей
    // Геттеры будут созданы динамически при инициализации views
    // Это позволит найти все модели с x-context.parent === modelName

    return function(self) {
        console.log(`[createChildGetters] Executing getter creator for model: ${modelName}`);
        const childGetters = {};

        // Используем getEnv для доступа к mstModelStore
        const env = getEnv(self);
        console.log(`[createChildGetters] Env exists:`, !!env, 'mstModelStore exists:', !!env?.mstModelStore);

        // Получаем все схемы для поиска дочерних моделей
        // Схемы хранятся в env.mstModelStore.schemas
        const schemas = env?.mstModelStore?.schemas || {};

        console.log(`[createChildGetters] Creating child getters for ${modelName}. Found schemas:`, Object.keys(schemas));

        for (const [childModelName, childSchema] of Object.entries(schemas)) {
            // Проверяем, является ли эта модель дочерней для текущей
            const parentName = childSchema?.['x-context']?.parent;
            if (parentName === modelName) {
                console.log(`[createChildGetters] Found child model ${childModelName} for parent ${modelName}`);
                // Создаем геттер для этой дочерней коллекции
                const getterName = childModelName; // например, 'calculations'

                Object.defineProperty(childGetters, getterName, {
                    get() {
                        console.log(`[GETTER ${getterName}] Called for ${modelName} with id=${self.id}`);
                        // Получаем env заново при каждом обращении к геттеру
                        const currentEnv = getEnv(self);
                        const mstModelStore = currentEnv?.mstModelStore;

                        // Используем универсальный метод поиска коллекции (сначала локальная, потом глобальная)
                        const childCollection = mstModelStore?.getCollection
                            ? mstModelStore.getCollection(childModelName)  // Новый универсальный метод
                            : mstModelStore?.collectionManagers?.[childModelName]; // Fallback для обратной совместимости

                        console.log(`[GETTER ${getterName}] Collection found:`, !!childCollection, 'Items:', childCollection?.items?.length);
                        if (!childCollection?.items) {
                            console.log(`[GETTER ${getterName}] No items, returning empty array`);
                            return [];
                        }

                        // Находим поле обратной ссылки в дочерней модели
                        const backRefField = findBackReference(childSchema, modelName);
                        console.log(`[createChildGetters] Back reference field for ${childModelName}: ${backRefField}`);

                        if (backRefField) {
                            // Фильтруем по обратной ссылке (например, projectId)
                            const filtered = childCollection.items.filter(item =>
                                item[backRefField] === self.id
                            );
                            console.log(`[createChildGetters] Filtered ${filtered.length} ${childModelName} for ${modelName} id=${self.id}`);
                            return filtered;
                        }

                        // Если обратная ссылка не найдена, возвращаем пустой массив
                        console.warn(`[createChildGetters] Back reference not found for ${childModelName} -> ${modelName}`);
                        return [];
                    },
                    enumerable: true,
                    configurable: true
                });
            }
        }

        return childGetters;
    };
}

// Создание computed полей из x-calculated определений (включая вложенные)
function createComputedFields(schema) {
    const computed = {};
    const { properties = {} } = schema;

    for (const [key, value] of Object.entries(properties)) {
        if (value['x-calculated']) {
            // Проверяем наличие x-formulas (типизированные формулы) или x-formula
            if (value['x-formulas']) {
                // Сохраняем типизированные формулы
                computed[key] = {
                    formulas: value['x-formulas'],
                    dependencies: value['x-dependencies'] || [],
                    isTyped: true
                };
            } else if (value['x-formula']) {
                // Сохраняем единую формулу
                computed[key] = {
                    formula: value['x-formula'],
                    dependencies: value['x-dependencies'] || [],
                    isTyped: false
                };
            }
        } else if (value.type === 'object' && value.properties) {
            // Обрабатываем вложенные объекты с вычисляемыми полями
            const nestedComputed = {};
            for (const [nestedKey, nestedValue] of Object.entries(value.properties)) {
                if (nestedValue['x-calculated'] && nestedValue['x-formula']) {
                    nestedComputed[nestedKey] = {
                        formula: nestedValue['x-formula'],
                        dependencies: nestedValue['x-dependencies'] || []
                    };
                }
            }
            // Если есть вычисляемые поля во вложенном объекте, создаем computed объект
            if (Object.keys(nestedComputed).length > 0) {
                computed[key] = {
                    type: 'object',
                    fields: nestedComputed
                };
            }
        }
    }

    return computed;
}

/**
 * Главная функция генерации MST модели из JSON Schema
 * @param {string} modelName - Имя модели (например, 'User')
 * @param {object} schema - JSON Schema объект
 * @returns {object} MST модель
 */
export function generateMSTModelFromSchema(modelName, schema) {
    // Создаем базовые props из схемы
    const props = createPropsFromSchema(schema);

    // Получаем вычисляемые поля (для будущего парсера формул)
    const computedFields = createComputedFields(schema);

    // Создаем базовую модель
    let model = types.model(modelName, props);
    
    // Получаем relation геттеры
    const relationGetters = createRelationGetters(schema, modelName);
    // Получаем функцию для создания child геттеров
    const childGettersCreator = createChildGetters(modelName);

    // Добавляем views для computed полей, relation геттеров и child геттеров
    if (Object.keys(computedFields).length > 0 || Object.keys(relationGetters).length > 0 || childGettersCreator) {
        model = model.views(self => {
            const views = {};

            // Для каждого computed поля создаем getter с парсером формул
            for (const [fieldName, config] of Object.entries(computedFields)) {
                // Проверяем, является ли поле denormalized (x-trigger-calculated)
                const fieldSchema = schema?.properties?.[fieldName];
                const isDenormalized = fieldSchema?.['x-trigger-calculated'];

                // Для denormalized полей НЕ создаем view - они остаются обычными props
                if (isDenormalized) {
                    continue;
                }

                if (config.type === 'object' && config.fields) {
                    // Создаем вложенный объект с вычисляемыми полями
                    Object.defineProperty(views, fieldName, {
                        get() {
                            const nestedObj = {};
                            for (const [nestedField, nestedConfig] of Object.entries(config.fields)) {
                                Object.defineProperty(nestedObj, nestedField, {
                                    get: createComputedGetter(nestedConfig.formula).bind(self),
                                    enumerable: true,
                                    configurable: true
                                });
                            }
                            return nestedObj;
                        },
                        enumerable: true,
                        configurable: true
                    });
                } else {
                    // Обычное вычисляемое поле
                    if (config.isTyped && config.formulas) {
                        // Типизированная формула - выбираем по типу
                        Object.defineProperty(views, fieldName, {
                            get() {
                                const type = self.type || 'works';
                                const formula = config.formulas[type];
                                if (!formula) {
                                    console.warn(`[MST] No formula for type "${type}" in field "${fieldName}"`);
                                    return 0;
                                }
                                return parseFormula(formula, self);
                            },
                            enumerable: true,
                            configurable: true
                        });
                    } else {
                        // Единая формула - всегда вычисляем
                        // (denormalized поля сюда не попадают, они пропущены выше)
                        Object.defineProperty(views, fieldName, {
                            get() {
                                return parseFormula(config.formula, self);
                            },
                            enumerable: true,
                            configurable: true
                        });
                    }
                }
            }

            // Добавляем relation геттеры с правильным контекстом
            for (const [getterName, getterFunc] of Object.entries(relationGetters)) {
                Object.defineProperty(views, getterName, {
                    get() {
                        // Вызываем функцию геттера с правильным контекстом self
                        return getterFunc.call(self);
                    },
                    enumerable: true,
                    configurable: true
                });
            }

            // Добавляем child геттеры (для иерархических связей через x-context.parent)
            if (childGettersCreator) {
                console.log(`[generateMSTModelFromSchema] Adding child getters for model: ${modelName}`);
                const childGetters = childGettersCreator(self);
                const descriptors = Object.getOwnPropertyDescriptors(childGetters);
                console.log(`[generateMSTModelFromSchema] Child getters found:`, Object.keys(descriptors));

                for (const [getterName, descriptor] of Object.entries(descriptors)) {
                    if (descriptor.get) {
                        console.log(`[generateMSTModelFromSchema] Adding child getter: ${getterName}`);
                        Object.defineProperty(views, getterName, descriptor);
                    }
                }
            }

            return views;
        });
    }
    
    // Добавляем базовые actions
    model = model.actions(self => {
        const baseActions = {
            // Обновление полей из JSON объекта
            update(data) {
                // Фильтруем computed поля в зависимости от их типа
                const filteredData = { ...data };

                if (schema && schema.properties) {
                    for (const [key, fieldSchema] of Object.entries(schema.properties)) {
                        if (fieldSchema['x-calculated']) {
                            if (fieldSchema['x-trigger-calculated']) {
                                // Denormalized поля - сохраняем значения в props
                                // Они обычные поля, можем обновлять
                                continue;
                            } else {
                                // Обычные computed поля - это views, нельзя присваивать
                                delete filteredData[key];
                            }
                        }
                    }
                }

                Object.assign(self, filteredData);
            },

            // Пересчет denormalized полей
            recalculate() {
                // Обновляем denormalized computed поля из вычисленных значений
                if (schema && schema.properties) {
                    const env = getEnv(self);
                    const mstModelStore = env?.mstModelStore;

                    // Собираем все необходимые коллекции один раз
                    const contextWithCollections = { ...self };
                    const allFormulas = [];

                    // Сначала проходим все поля и собираем необходимые коллекции
                    for (const [fieldName, fieldSchema] of Object.entries(schema.properties)) {
                        if (fieldSchema['x-calculated'] && fieldSchema['x-trigger-calculated']) {
                            const formula = fieldSchema['x-formula'];
                            if (!formula) continue;

                            allFormulas.push({ fieldName, formula });

                            // Проверяем, используются ли дочерние коллекции в формуле
                            const collectionPattern = /(\b[a-z]+)(?=\.|\[)/gi;
                            const matches = formula.match(collectionPattern);

                            if (matches) {
                                for (const collectionName of matches) {
                                    // Если коллекция еще не добавлена в контекст
                                    if (!(collectionName in contextWithCollections)) {
                                        const collection = mstModelStore?.getCollection?.(collectionName);
                                        if (collection && collection.items) {
                                            contextWithCollections[collectionName] = collection.items;
                                            console.log(`[recalculate] Добавлена коллекция ${collectionName} в контекст, items: ${collection.items.length}`);
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Теперь вычисляем все формулы с полным контекстом
                    for (const { fieldName, formula } of allFormulas) {
                        const computedValue = parseFormula(formula, contextWithCollections);
                        if (computedValue !== undefined) {
                            console.log(`[recalculate] Обновляем ${fieldName} = ${computedValue}`);
                            self[fieldName] = computedValue;
                        }
                    }
                }
            },

            // Установка значения поля с преобразованием типов
            setField(fieldName, value) {
                if (fieldName in self) {
                    // Получаем тип поля из схемы
                    const fieldSchema = schema.properties?.[fieldName];
                    if (fieldSchema) {
                        // Преобразуем значение в соответствии с типом
                        if (fieldSchema.type === 'number' || fieldSchema.type === 'integer') {
                            const num = Number(value);
                            if (!isNaN(num)) {
                                self[fieldName] = fieldSchema.type === 'integer' ? Math.floor(num) : num;
                            }
                        } else if (fieldSchema.type === 'boolean') {
                            self[fieldName] = Boolean(value);
                        } else if (fieldSchema.type === 'string' && typeof value !== 'string') {
                            self[fieldName] = String(value);
                        } else {
                            self[fieldName] = value;
                        }
                    } else {
                        self[fieldName] = value;
                    }
                }
            }
        };

        // Генерируем actions из x-operations
        const generatedActions = generateActionsFromOperations(self, schema);

        console.log(`[schemaToMST] Generating actions for model "${modelName}":`, {
            hasXOperations: !!schema['x-operations'],
            xOperations: schema['x-operations'],
            generatedActionsKeys: Object.keys(generatedActions)
        });

        return { ...baseActions, ...generatedActions };
    });
    
    return model;
}

/**
 * Создание коллекции моделей для использования в CollectionManager
 */
export function createModelCollection(modelName, schema) {
    const ItemModel = generateMSTModelFromSchema(modelName, schema);
    
    const CollectionModel = types
        .model(`${modelName}Collection`, {
            items: types.array(ItemModel),
            isLoading: types.optional(types.boolean, false),
            error: types.maybeNull(types.string),
            // Метаданные коллекции
            entityName: types.optional(types.string, ''),
            entitySingular: types.optional(types.string, ''),
            apiEndpoint: types.optional(types.string, ''),
            // Для пагинации и фильтров
            currentPage: types.optional(types.number, 1),
            totalPages: types.optional(types.number, 1),
            pageSize: types.optional(types.number, 20),
            filters: types.optional(types.frozen(), {}),
            sorting: types.optional(types.frozen(), {}),
            // Метаданные для хранения информации о связях (parent-child)
            metadata: types.optional(types.frozen({}), {})
        })
        .volatile(() => ({
            // Метаданные модели для UI
            modelSchema: null,
            // Сохраняем конфиг роута для вычисления прав
            routeConfig: null
        }))
        .views(self => ({
            get count() {
                return self.items.length;
            },
            
            getById(id) {
                return self.items.find(item => item.id === id);
            },
            
            get isEmpty() {
                return self.items.length === 0;
            },
            
            // Вспомогательный геттер для получения прав роли - DRY!
            get _rolePermissions() {
                const env = getEnv(self);
                const rootStore = env.rootStore;
                const mstModelStore = env.mstModelStore;
                const userRole = rootStore?.auth?.userRole; // ИСПОЛЬЗУЕМ COMPUTED ПОЛЕ!
                
                // Базовая проверка роли
                if (!userRole) return { role: null, operations: [], hasPageAccess: false };
                
                // Проверяем доступ к странице
                const hasPageAccess = !self.routeConfig?.access || 
                    self.routeConfig.access.roles?.includes(userRole);
                
                // Получаем операции из определения роли
                const routesModel = mstModelStore?.schemas?.routes;
                const roleDefinition = routesModel?.roles?.[userRole];
                const operations = roleDefinition?.operations || [];
                
                return { role: userRole, operations, hasPageAccess };
            },
            
            // Computed права - реактивно пересчитываются при изменении роли
            get canCreate() {
                const perms = self._rolePermissions;
                return perms.hasPageAccess && perms.operations.includes('POST');
            },
            
            get canUpdate() {
                const perms = self._rolePermissions;
                return perms.hasPageAccess && perms.operations.includes('PUT');
            },
            
            get canDelete() {
                const perms = self._rolePermissions;
                return perms.hasPageAccess && perms.operations.includes('DELETE');
            },
            
            get canRead() {
                const perms = self._rolePermissions;
                // Чтение разрешено по умолчанию если есть доступ к странице
                return perms.hasPageAccess && (perms.operations.includes('GET') || perms.role === null);
            },
            
            get hasSpecialOperations() {
                const perms = self._rolePermissions;
                return perms.hasPageAccess && perms.operations.includes('SPEC');
            }
        }))
        .actions(self => ({

            // Нормализация данных элемента согласно схеме
            normalizeItemData(item) {
                if (!item || typeof item !== 'object') return item;
                
                // Получаем схему из environment если она есть
                const env = getEnv(self);
                const mstModelStore = env.mstModelStore;
                const schema = mstModelStore?.schemas?.[self.entityName];
                
                if (!schema || !schema.properties) return item;
                
                // Создаем нормализованный объект
                const normalized = { ...item };
                
                // Преобразуем каждое поле согласно типу в схеме
                for (const [key, fieldSchema] of Object.entries(schema.properties)) {
                    if (key in normalized && normalized[key] !== null && normalized[key] !== undefined) {
                        const value = normalized[key];
                        const type = fieldSchema.type;
                        
                        // Преобразование типов
                        if (type === 'number' || type === 'integer') {
                            const num = Number(value);
                            if (!isNaN(num)) {
                                normalized[key] = type === 'integer' ? Math.floor(num) : num;
                            }
                        } else if (type === 'boolean') {
                            normalized[key] = Boolean(value);
                        } else if (type === 'string' && typeof value !== 'string') {
                            normalized[key] = String(value);
                        }
                        // Для массивов и объектов оставляем как есть
                    }
                }
                
                return normalized;
            },
            
            load: flow(function* (options = {}) {
                // Поддержка обратной совместимости: если передан boolean, это silent
                let silent = false;
                let frozen = false;

                if (typeof options === 'boolean') {
                    silent = options;
                } else {
                    silent = options.silent || false;
                    frozen = options.frozen || false;
                }

                // При silent обновлении не показываем индикатор загрузки
                if (!silent) {
                    self.isLoading = true;
                }
                self.error = null;
                try {
                    let data = [];
                    
                    // Обычная загрузка через API
                    const env = getEnv(self);
                    const rootStore = env.rootStore;
                    if (!rootStore || !rootStore.apiClient) {
                        throw new Error('API client not found in environment');
                    }
                    const response = yield rootStore.apiClient.get(self.apiEndpoint);
                    
                    // Сервер возвращает данные в массиве или объекте
                    data = response.data || response || [];
                    
                    // Если сервер вернул объект вместо массива - пропускаем
                    // (например, для models возвращается объект схем)
                    if (!Array.isArray(data)) {
                        console.log(`[CollectionModel] Skipping non-array data for ${self.entityName}`);
                        data = [];
                    } else {
                        // Преобразование типов для каждого элемента
                        data = data.map(item => self.normalizeItemData(item));
                    }
                    
                    console.log('[CollectionModel] Loading data for', self.entityName, ':', data);
                    
                    // Умное обновление - не перерисовываем всё, а обновляем только изменения
                    const existingIds = new Set(self.items.map(item => item.id));
                    const newIds = new Set(data.map(item => item.id));
                    
                    // Удаляем элементы которых больше нет
                    const toRemove = self.items.filter(item => !newIds.has(item.id));
                    toRemove.forEach(item => {
                        const index = self.items.indexOf(item);
                        if (index !== -1) self.items.splice(index, 1);
                    });
                    
                    // Обновляем существующие и добавляем новые
                    data.forEach((newItem, index) => {
                        const existingItem = self.items.find(item => item.id === newItem.id);
                        if (existingItem) {
                            // В frozen режиме НЕ фильтруем computed поля - они уже вычислены на сервере
                            // Просто используем данные как есть, но проверяем изменения
                            const snapshot = getSnapshot(existingItem);
                            if (JSON.stringify(snapshot) !== JSON.stringify(newItem)) {
                                existingItem.update(newItem);
                            }
                        } else {
                            // Добавляем новый элемент в правильную позицию
                            console.log(`[CollectionModel] Adding new item at index ${index}:`, newItem.id);
                            console.log(`[CollectionModel] Before splice: items.length = ${self.items.length}`);
                            self.items.splice(index, 0, newItem);
                            console.log(`[CollectionModel] After splice: items.length = ${self.items.length}`);
                            console.log(`[CollectionModel] Item added successfully:`, self.items.find(item => item.id === newItem.id) ? 'YES' : 'NO');
                        }
                    });
                    
                    // Сортируем если порядок изменился
                    const currentOrder = self.items.map(item => item.id).join(',');
                    const newOrder = data.map(item => item.id).join(',');
                    if (currentOrder !== newOrder) {
                        // Пересортировываем на месте
                        const sorted = data.map(d => self.items.find(item => item.id === d.id)).filter(Boolean);
                        self.items.replace(sorted);
                    }
                    
                    return data;
                } catch (error) {
                    console.error(`Failed to load ${self.entityName}:`, error);
                    self.error = error.message;
                    return null;
                } finally {
                    self.isLoading = false;
                }
            }),
            
            setItems(items) {
                self.items.replace(items);
            },

            addItem(item) {
                // Проверяем, нет ли уже такого элемента
                const existing = self.getById(item.id);
                if (!existing) {
                    self.items.push(item);
                }
                return self.getById(item.id);
            },

            // Загрузка одного элемента по ID
            loadById: flow(function* (id) {
                // Проверяем кэш
                const cached = self.getById(id);
                if (cached) {
                    return cached;
                }

                const env = getEnv(self);
                const { apiClient } = env.rootStore || {};

                if (!apiClient) {
                    console.error('[CollectionModel] No apiClient in environment');
                    return null;
                }

                try {
                    self.isLoading = true;
                    const endpoint = `${self.apiEndpoint}/${id}`;
                    const response = yield apiClient.get(endpoint);

                    if (response) {
                        // Нормализуем и добавляем в коллекцию
                        const normalized = self.normalizeItemData(response);
                        return self.addItem(normalized);
                    }
                    return null;
                } catch (error) {
                    console.error(`Failed to load ${self.entityName} by id ${id}:`, error);
                    self.error = error.message;
                    return null;
                } finally {
                    self.isLoading = false;
                }
            }),

            // Загрузка расчетов по ID проекта
            loadByProjectId: flow(function* (projectId) {
                const env = getEnv(self);
                const { apiClient } = env.rootStore || {};

                if (!apiClient) {
                    console.error('[CollectionModel] No apiClient in environment');
                    return null;
                }

                try {
                    self.isLoading = true;
                    // Специальный endpoint для загрузки расчетов проекта
                    const endpoint = `/api/projects/${projectId}/calculations`;
                    console.log('[loadByProjectId] Loading from endpoint:', endpoint);
                    const response = yield apiClient.get(endpoint);

                    if (response && Array.isArray(response)) {
                        // Заменяем текущие элементы на элементы проекта
                        const normalized = response.map(item => self.normalizeItemData(item));
                        console.log('[loadByProjectId] Received items:', response.length, 'for project:', projectId);
                        self.setItems(normalized);
                        return self.items;
                    }
                    return [];
                } catch (error) {
                    console.error(`Failed to load calculations for project ${projectId}:`, error);
                    self.error = error.message;
                    return [];
                } finally {
                    self.isLoading = false;
                }
            }),

            // Очистка устаревших данных
            clearStaleData(keepCondition) {
                // keepCondition - функция, которая определяет какие элементы оставить
                if (typeof keepCondition === 'function') {
                    const itemsToKeep = self.items.filter(keepCondition);
                    self.items.replace(itemsToKeep);
                }
            },

            // Полная очистка коллекции при уходе со страницы
            cleanup() {
                // Проверяем, есть ли несохраненные изменения
                const hasModified = self.items.some(item => item.isModified === true);
                if (!hasModified) {
                    self.clear();
                } else {
                    console.warn(`[CollectionModel] Skipping cleanup for ${self.entityName} - has modified items`);
                }
            },
            
            updateItem(id, data) {
                const item = self.getById(id);
                if (item) {
                    item.update(data);
                }
            },
            
            removeItem(id) {
                const index = self.items.findIndex(item => item.id === id);
                if (index !== -1) {
                    self.items.splice(index, 1);
                }
            },
            
            setLoading(loading) {
                self.isLoading = loading;
            },
            
            setError(error) {
                self.error = error;
            },
            
            clear() {
                self.items.clear();
                self.error = null;
                self.isLoading = false;
            },
            
            // Установка метаданных из routes конфигурации
            setPermissions(permissions) {
                // Теперь сохраняем только метаданные, права вычисляются в views
                if (permissions.modelSchema !== undefined) self.modelSchema = permissions.modelSchema;
                if (permissions.routeConfig !== undefined) self.routeConfig = permissions.routeConfig;
            },
            
            // Обновление поля для первого элемента (для system и форм)
            updateField(fieldName, value) {
                if (self.items.length > 0) {
                    const item = self.items[0];
                    if (item && item.setField) {
                        // Используем action элемента для обновления
                        item.setField(fieldName, value);
                    }
                }
            },
            
            // Подготовка данных для создания с учетом наследования
            prepareCreateData: flow(function* (formData, operationContext = {}) {
                const env = getEnv(self);
                const mstModelStore = env.mstModelStore;
                const rootStore = env.rootStore;
                const schema = mstModelStore?.schemas?.[self.entityName];

                if (!schema || !schema.properties) {
                    return formData;
                }

                const preparedData = { ...formData };

                // Подготавливаем контекст со специальными источниками
                const context = {
                    // session всегда доступна
                    session: {
                        currentUserId: rootStore?.auth?.user?.id,
                        currentUser: rootStore?.auth?.user
                    },
                    // Добавляем переданный контекст (project, parent и т.д.)
                    ...operationContext
                };

                // Обрабатываем наследуемые поля согласно схеме
                for (const [fieldName, fieldSchema] of Object.entries(schema.properties)) {
                    if (fieldSchema['x-inherited'] === true && fieldSchema['x-inheritsFrom']) {
                        const sourceName = fieldSchema['x-inheritsFrom'];
                        const inheritance = fieldSchema['x-inheritance'] || {};

                        // Получаем источник данных
                        let sourceData;

                        // Проверяем контекст операции
                        if (context[sourceName]) {
                            sourceData = context[sourceName];
                        } else {
                            // Если не в контексте - ищем коллекцию по имени
                            const sourceCollection = mstModelStore.getCollectionManager(sourceName);
                            if (sourceCollection && sourceCollection.items && sourceCollection.items.length > 0) {
                                sourceData = sourceCollection.items[0];
                            }
                        }

                        // Если источник найден, применяем маппинг
                        if (sourceData) {
                            for (const [targetField, sourcePath] of Object.entries(inheritance)) {
                                // Получаем значение по пути (поддержка вложенных путей через точку)
                                const pathParts = sourcePath.split('.');
                                let value = sourceData;

                                for (const part of pathParts) {
                                    if (value && typeof value === 'object') {
                                        value = value[part];
                                    } else {
                                        value = undefined;
                                        break;
                                    }
                                }

                                // Устанавливаем значение если оно определено
                                if (value !== undefined) {
                                    preparedData[targetField] = value;
                                }
                            }
                        }
                    }
                }

                // Удаляем поля которые генерируются на сервере
                for (const [fieldName, fieldSchema] of Object.entries(schema.properties)) {
                    if (fieldSchema['x-generated'] === true || fieldSchema['x-calculated'] === true) {
                        delete preparedData[fieldName];
                    }
                }

                return preparedData;
            }),
            
            // Подготовка данных для обновления
            prepareUpdateData: flow(function* (id, formData) {
                const existingItem = self.getById(id);
                if (!existingItem) {
                    throw new Error('Item not found');
                }

                // Объединяем с существующими данными
                // formData уже является обычным объектом с обновленными полями
                const preparedData = {
                    ...formData,
                    id // Сохраняем id
                };
                
                // Удаляем системные поля которые не должны обновляться
                delete preparedData.createdAt;
                delete preparedData.author; // Автор не меняется
                
                const env = getEnv(self);
                const mstModelStore = env.mstModelStore;
                const schema = mstModelStore?.schemas?.[self.entityName];
                
                if (schema && schema.properties) {
                    // Удаляем вычисляемые поля, но НЕ удаляем id при обновлении
                    for (const [fieldName, fieldSchema] of Object.entries(schema.properties)) {
                        // ID нужен для обновления, не удаляем его
                        if (fieldName === 'id') continue;

                        if (fieldSchema['x-generated'] === true || fieldSchema['x-calculated'] === true) {
                            delete preparedData[fieldName];
                        }
                    }
                }
                
                return preparedData;
            }),
            
            // CRUD методы
            create: flow(function* (item) {
                if (!self.canCreate) {
                    self.error = 'Нет прав на создание';
                    return false;
                }
                
                self.isLoading = true;
                self.error = null;
                
                try {
                    // Подготавливаем данные с учетом наследования
                    // TODO: operationContext должен передаваться из вызывающего кода
                    const preparedData = yield self.prepareCreateData(item, {});
                    
                    const env = getEnv(self);
                    const rootStore = env.rootStore;
                    yield rootStore.apiClient.post(self.apiEndpoint, preparedData);
                    // Перезагружаем данные после создания
                    yield self.load();
                    return true;
                } catch (error) {
                    console.error('Failed to create:', error);
                    self.error = error.message;
                    return false;
                } finally {
                    self.isLoading = false;
                }
            }),
            
            save: flow(function* (item) {
                if (!self.canUpdate) {
                    self.error = 'Нет прав на обновление';
                    return false;
                }

                self.isLoading = true;
                self.error = null;

                try {
                    // Подготавливаем данные для обновления
                    const preparedData = yield self.prepareUpdateData(item.id, item);

                    const env = getEnv(self);
                    const rootStore = env.rootStore;

                    // Для всех сущностей единообразно добавляем id если он есть
                    const url = preparedData.id
                        ? `${self.apiEndpoint}/${preparedData.id}`
                        : self.apiEndpoint;

                    yield rootStore.apiClient.put(url, preparedData);
                    // Обновление придет через WebSocket
                    return true;
                } catch (error) {
                    console.error('Failed to save:', error);
                    self.error = error.message;
                    return false;
                } finally {
                    self.isLoading = false;
                }
            }),
            
            delete: flow(function* (id) {
                if (!self.canDelete) {
                    self.error = 'Нет прав на удаление';
                    return false;
                }
                
                self.isLoading = true;
                self.error = null;
                
                try {
                    const env = getEnv(self);
                    const rootStore = env.rootStore;
                    yield rootStore.apiClient.delete(`${self.apiEndpoint}/${id}`);
                    // Удаление придет через WebSocket
                    return true;
                } catch (error) {
                    console.error('Failed to delete:', error);
                    self.error = error.message;
                    return false;
                } finally {
                    self.isLoading = false;
                }
            })
        }));
    
    return { ItemModel, CollectionModel };
}